/**
 * InsightRow — json-render web wrapper (enhanced styling)
 *
 * Web implementation for the json-render catalog.
 * Visual spec: Sally (Discourse #3543/4) — Section 6
 *
 * The real IssuesSection lives in apps/mobile/ (can't import from packages/ui).
 * This web wrapper uses glassmorphism-inspired card treatment matching the
 * app's visual language: subtle background, rounded corners, soft borders.
 *
 * Directional trend arrow (up/down/right) communicates direction via both
 * icon AND color (accessibility requirement per Sally spec).
 *
 * Task: [#1158.SDUI-M.P3.4a]
 * Agent: Diego <diego@liftping.net>
 */
"use client";

import React from "react";
import { cn } from "../../lib/utils";
import type { InsightRowProps } from "../schema";

const trendConfig = {
  up: {
    icon: "\u2191",
    color: "text-drama-safe",
    ariaLabel: "trending up",
  },
  down: {
    icon: "\u2193",
    color: "text-drama-extreme",
    ariaLabel: "trending down",
  },
  neutral: {
    icon: "\u2192",
    color: "text-muted-foreground",
    ariaLabel: "neutral trend",
  },
} as const;

export function InsightRow({
  title,
  description,
  trend,
  changePercent,
}: InsightRowProps) {
  const trendCfg = trend ? trendConfig[trend] : null;

  return (
    <div className="flex items-start justify-between gap-3 rounded-tier bg-white/60 backdrop-blur-sm border border-border/50 px-4 py-3 shadow-sm">
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-semibold text-foreground truncate">{title}</span>
        <span className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">{description}</span>
      </div>
      <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
        {changePercent !== undefined && (
          <span className={cn("text-sm font-medium tabular-nums", trendCfg?.color ?? "text-foreground")}>
            {changePercent > 0 ? "+" : ""}{changePercent}%
          </span>
        )}
        {trendCfg && (
          <span
            className={cn("text-base font-bold leading-none", trendCfg.color)}
            aria-label={trendCfg.ariaLabel}
          >
            {trendCfg.icon}
          </span>
        )}
      </div>
    </div>
  );
}
